/**
 * Builds the recruiter app provider tree and route content.
 *
 * 1. Create app data and settings providers.
 * 2. Mount the download reason dialog inside Apollo and snackbar providers.
 * 3. Render the selected recruiter routes.
 * The outer locale and snackbar providers are in `shared/app/BaseApp.tsx`.
 */

import React from 'react';
import type { FC } from 'react';
import { Provider } from 'react-redux';

import ConfirmationDialogProvider from 'src/components/Dialog/ConfirmationDialog/ConfirmationDialogProvider';
import DownloadReasonDialogProvider from 'src/components/Dialog/DownloadReasonDialog/DownloadReasonDialogProvider';

import { AgentDebugLogsProvider } from 'src/contexts/AgentDebugLogsContext';
import ApolloProvider from 'src/contexts/ApolloProvider';
import OnboardingProvider from 'src/contexts/OnboardingProvider';
import ReactQueryClientProvider from 'src/contexts/ReactQueryClientProvider';
import { SettingsProvider } from 'src/contexts/SettingsContext';
import { SupportedLocalesProvider } from 'src/contexts/SupportedLocalesProvider';
import { TaskTimelineVisibilityProvider } from 'src/contexts/TaskTimelineVisibilityProvider';

import { useSyncEmployeeLocale } from 'src/hooks/api/employee/useSyncEmployeeLocale';

import MaintenancePage from 'src/views-new/MaintenancePage';

import routes, { renderRoutes } from 'src/routes';
import store from 'src/store';

import HiringDashboardSessionActivityTimeout from './HiringDashboardSessionActivityTimeout';

/**
 * Syncs the employee's locale from the backend once Apollo is available.
 * Must be rendered inside ApolloProvider.
 */
const SyncEmployeeLocale: FC<{ children?: React.ReactNode }> = ({ children }) => {
  useSyncEmployeeLocale();
  return <>{children}</>;
};

const RecruitingApp: FC = () => {
  return (
    <Provider store={store}>
      <AgentDebugLogsProvider>
        <SettingsProvider>
          <OnboardingProvider>
            <ReactQueryClientProvider>
              <ApolloProvider>
                <SupportedLocalesProvider>
                  <SyncEmployeeLocale>
                    <HiringDashboardSessionActivityTimeout />
                    <ConfirmationDialogProvider>
                      <DownloadReasonDialogProvider>
                        <TaskTimelineVisibilityProvider>
                          <MaintenancePage>
                            <>{renderRoutes(routes)}</>
                          </MaintenancePage>
                        </TaskTimelineVisibilityProvider>
                      </DownloadReasonDialogProvider>
                    </ConfirmationDialogProvider>
                  </SyncEmployeeLocale>
                </SupportedLocalesProvider>
              </ApolloProvider>
            </ReactQueryClientProvider>
          </OnboardingProvider>
        </SettingsProvider>
      </AgentDebugLogsProvider>
    </Provider>
  );
};

export default RecruitingApp;
